import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Grid,
  GridItem,
  Badge,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Divider,
  useToast,
  Icon,
} from "@chakra-ui/react";
import { CheckIcon, TimeIcon, RepeatIcon } from "@chakra-ui/icons";
import Navbar from "../../common/Navbar";

// ============================================
// TYPES
// ============================================
interface Fact {
  id: string;
  text: string;
  number: number;
  isPrePlaced: boolean;
  gridPosition?: { row: number; col: number };
}

interface Puzzle {
  facts: Fact[];
  targetSums: {
    rows: [number, number, number];
    cols: [number, number, number];
  };
}

type GridState = (Fact | null)[][];

interface RevealedState {
  rows: [boolean, boolean, boolean];
  cols: [boolean, boolean, boolean];
}

// ============================================
// SAMPLE PUZZLE DATA
// ============================================
const samplePuzzle: Puzzle = {
  facts: [
    { id: "1", text: "Spider legs", number: 8, isPrePlaced: true, gridPosition: { row: 0, col: 0 } },
    { id: "2", text: "Michael Jordan's number", number: 23, isPrePlaced: false },
    { id: "3", text: "Supreme Court justices", number: 9, isPrePlaced: true, gridPosition: { row: 1, col: 1 } },
    { id: "4", text: "Deadly sins", number: 7, isPrePlaced: true, gridPosition: { row: 2, col: 2 } },
    { id: "5", text: "Harry Potter books", number: 7, isPrePlaced: false },
    { id: "6", text: "Days in a week", number: 7, isPrePlaced: false },
    { id: "7", text: "Beatles members", number: 4, isPrePlaced: false },
    { id: "8", text: "Kobe's championships", number: 5, isPrePlaced: false },
    { id: "9", text: "Planets in solar system", number: 8, isPrePlaced: false },
  ],
  targetSums: {
    rows: [40, 21, 22],
    cols: [19, 41, 23],
  },
};

// ============================================
// UTILITIES
// ============================================
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

// ============================================
// FACT CARD COMPONENT
// ============================================
interface FactCardProps {
  fact: Fact;
  isPrePlaced: boolean;
  isRevealed: boolean;
  isDragging: boolean;
  onDragStart: (e: React.DragEvent, fact: Fact) => void;
  onDragEnd: (e: React.DragEvent) => void;
  inGrid?: boolean;
}

const FactCard = (props: FactCardProps) => {
  const {
    fact,
    isPrePlaced,
    isRevealed,
    isDragging,
    onDragStart,
    onDragEnd,
    inGrid = false,
  } = props;
  return (
    <Box
      draggable={!isPrePlaced}
      onDragStart={(e: React.DragEvent<HTMLDivElement>) => onDragStart(e, fact)}
      onDragEnd={onDragEnd}
      bg={isPrePlaced ? "teal.100" : "white"}
      border="2px solid"
      borderColor={isPrePlaced ? "teal.400" : "gray.300"}
      borderRadius="md"
      p={inGrid ? 2 : 3}
      cursor={isPrePlaced ? "default" : "grab"}
      opacity={isDragging ? 0.5 : 1}
      transition="all 0.2s"
      _hover={!isPrePlaced ? { borderColor: "teal.500", shadow: "md" } : {}}
      minH={inGrid ? "80px" : "60px"}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      textAlign="center"
      position="relative"
      w="100%"
      h="100%"
    >
      <Text fontSize={inGrid ? "xs" : "sm"} fontWeight="medium" lineHeight="short">
        {fact.text}
      </Text>
      {(isRevealed || isPrePlaced) && (
        <Badge
          colorScheme="teal"
          fontSize="lg"
          mt={1}
          px={2}
          py={1}
          borderRadius="full"
          animation={isRevealed && !isPrePlaced ? "fadeIn 0.5s ease-in" : undefined}
        >
          {fact.number}
        </Badge>
      )}
    </Box>
  );
};

// ============================================
// GRID CELL COMPONENT
// ============================================
interface GridCellProps {
  row: number;
  col: number;
  fact: Fact | null;
  isPrePlaced: boolean;
  isRevealed: boolean;
  isDragOver: boolean;
  draggingFact: Fact | null;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, row: number, col: number) => void;
  onDragStart: (e: React.DragEvent, fact: Fact) => void;
  onDragEnd: (e: React.DragEvent) => void;
}

const GridCell = (props: GridCellProps) => {
  const {
    row,
    col,
    fact,
    isPrePlaced,
    isRevealed,
    isDragOver,
    draggingFact,
    onDragOver,
    onDragLeave,
    onDrop,
    onDragStart,
    onDragEnd,
  } = props;
  return (
    <Box
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={(e: React.DragEvent<HTMLDivElement>) => onDrop(e, row, col)}
      bg={isDragOver ? "teal.50" : "gray.50"}
      border="2px dashed"
      borderColor={isDragOver ? "teal.400" : "gray.300"}
      borderRadius="md"
      minH={{ base: "90px", md: "100px" }}
      minW={{ base: "90px", md: "100px" }}
      display="flex"
      justifyContent="center"
      alignItems="center"
      transition="all 0.2s"
      p={1}
    >
      {fact ? (
        <FactCard
          fact={fact}
          isPrePlaced={isPrePlaced}
          isRevealed={isRevealed}
          isDragging={draggingFact?.id === fact.id}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          inGrid
        />
      ) : (
        <Text color="gray.400" fontSize="sm">
          Drop here
        </Text>
      )}
    </Box>
  );
};

// ============================================
// NUMBER BANK COMPONENT
// ============================================
interface NumberBankProps {
  facts: Fact[];
  revealedFactIds: Set<string>;
}

const NumberBank = (props: NumberBankProps) => {
  const { facts, revealedFactIds } = props;
  // Sort facts by number for display
  const sortedFacts = [...facts].sort((a, b) => a.number - b.number);

  return (
    <Box
      bg="white"
      border="2px solid"
      borderColor="gray.200"
      borderRadius="lg"
      p={4}
      minW={{ base: "100%", md: "150px" }}
    >
      <Heading size="sm" mb={3} textAlign="center">
        Number Bank
      </Heading>
      <Grid templateColumns="repeat(3, 1fr)" gap={2}>
        {sortedFacts.map((fact) => {
          const isRevealed = revealedFactIds.has(fact.id);
          
          return (
            <GridItem key={fact.id}>
              <Box
                bg={isRevealed ? "gray.100" : "teal.500"}
                color={isRevealed ? "gray.400" : "white"}
                borderRadius="md"
                p={2}
                textAlign="center"
                fontWeight="bold"
                textDecoration={isRevealed ? "line-through" : "none"}
                transition="all 0.3s"
                fontSize="sm"
              >
                {fact.number}
              </Box>
            </GridItem>
          );
        })}
      </Grid>
    </Box>
  );
};

// ============================================
// SUM DISPLAY COMPONENT
// ============================================
interface SumDisplayProps {
  target: number;
  current: number | null;
  isCorrect: boolean;
  direction: "row" | "col";
}

const SumDisplay = (props: SumDisplayProps) => {
  const { target, current, isCorrect } = props;
  // Show red if there's a current sum but it's wrong, green if correct, gray if incomplete
  const hasAllCells = current !== null;
  const isIncorrect = hasAllCells && !isCorrect;
  
  let bgColor = "gray.100";
  let borderColor = "gray.300";
  let textColor = "gray.600";
  
  if (isCorrect) {
    bgColor = "green.100";
    borderColor = "green.400";
    textColor = "green.600";
  } else if (isIncorrect) {
    bgColor = "red.100";
    borderColor = "red.400";
    textColor = "red.600";
  }
  
  return (
    <Box
      bg={bgColor}
      border="2px solid"
      borderColor={borderColor}
      borderRadius="md"
      px={3}
      py={2}
      minW="50px"
      textAlign="center"
      display="flex"
      alignItems="center"
      justifyContent="center"
      gap={1}
    >
      <Text fontWeight="bold" color={textColor}>
        {target}
      </Text>
      {isCorrect && <Icon as={CheckIcon} color="green.500" boxSize={4} />}
    </Box>
  );
};

// ============================================
// SIDEBAR COMPONENT
// ============================================
interface SidebarProps {
  availableFacts: Fact[];
  draggingFact: Fact | null;
  onDragStart: (e: React.DragEvent, fact: Fact) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  isDragOver: boolean;
}

const Sidebar = (props: SidebarProps) => {
  const {
    availableFacts,
    draggingFact,
    onDragStart,
    onDragEnd,
    onDragOver,
    onDrop,
    isDragOver,
  } = props;
  return (
    <Box
      bg="gray.50"
      border="2px solid"
      borderColor={isDragOver ? "teal.400" : "gray.200"}
      borderRadius="lg"
      p={4}
      minW={{ base: "100%", md: "200px" }}
      onDragOver={onDragOver}
      onDrop={onDrop}
      transition="all 0.2s"
    >
      <Heading size="sm" mb={3} textAlign="center">
        Available Facts
      </Heading>
      <VStack spacing={2}>
        {availableFacts.length === 0 ? (
          <Text color="gray.400" fontSize="sm" textAlign="center">
            All facts placed!
          </Text>
        ) : (
          availableFacts.map((fact) => (
            <FactCard
              key={fact.id}
              fact={fact}
              isPrePlaced={false}
              isRevealed={false}
              isDragging={draggingFact?.id === fact.id}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
            />
          ))
        )}
      </VStack>
    </Box>
  );
};

// ============================================
// STATS MODAL COMPONENT
// ============================================
interface StatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  moves: number;
  time: number;
  onPlayAgain: () => void;
}

const StatsModal = (props: StatsModalProps) => {
  const {
    isOpen,
    onClose,
    moves,
    time,
    onPlayAgain,
  } = props;
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent mx={4}>
        <ModalHeader textAlign="center" pb={0}>
          🎉 Puzzle Complete!
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={4}>
            <StatGroup w="100%">
              <Stat textAlign="center">
                <StatLabel>
                  <Icon as={RepeatIcon} mr={1} />
                  Moves
                </StatLabel>
                <StatNumber color="teal.500">{moves}</StatNumber>
              </Stat>
              <Stat textAlign="center">
                <StatLabel>
                  <Icon as={TimeIcon} mr={1} />
                  Time
                </StatLabel>
                <StatNumber color="teal.500">{formatTime(time)}</StatNumber>
              </Stat>
            </StatGroup>

            <Divider />

            <Box textAlign="center" w="100%">
              <Text fontSize="sm" color="gray.600" mb={2}>
                Daily Average Comparison
              </Text>
              <HStack justify="center" spacing={6}>
                <VStack spacing={0}>
                  <Text fontSize="xs" color="gray.500">
                    Avg Moves
                  </Text>
                  <Text fontWeight="bold">~12</Text>
                </VStack>
                <VStack spacing={0}>
                  <Text fontSize="xs" color="gray.500">
                    Avg Time
                  </Text>
                  <Text fontWeight="bold">~3:30</Text>
                </VStack>
              </HStack>
            </Box>

            <Button
              colorScheme="teal"
              w="100%"
              onClick={onPlayAgain}
              leftIcon={<RepeatIcon />}
            >
              Play Again
            </Button>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// ============================================
// MAIN GAME COMPONENT
// ============================================
const ByTheNumbers: React.FC = () => {
  const [puzzle] = useState<Puzzle>(samplePuzzle);
  const [grid, setGrid] = useState<GridState>([
    [null, null, null],
    [null, null, null],
    [null, null, null],
  ]);
  const [availableFacts, setAvailableFacts] = useState<Fact[]>([]);
  const [draggingFact, setDraggingFact] = useState<Fact | null>(null);
  const [dragOverCell, setDragOverCell] = useState<{ row: number; col: number } | null>(null);
  const [sidebarDragOver, setSidebarDragOver] = useState(false);
  const [revealed, setRevealed] = useState<RevealedState>({
    rows: [false, false, false],
    cols: [false, false, false],
  });
  const [moves, setMoves] = useState(0);
  const [time, setTime] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [revealedFactIds, setRevealedFactIds] = useState<Set<string>>(new Set());

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  // Initialize the game
  useEffect(() => {
    initializeGame();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puzzle]);

  // Timer
  useEffect(() => {
    if (!isComplete) {
      timerRef.current = setInterval(() => {
        setTime((prev: number) => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isComplete]);

  const initializeGame = () => {
    // Reset state
    const newGrid: GridState = [
      [null, null, null],
      [null, null, null],
      [null, null, null],
    ];
    const available: Fact[] = [];
    const initialRevealedIds = new Set<string>();

    puzzle.facts.forEach((fact: Fact) => {
      if (fact.isPrePlaced && fact.gridPosition) {
        newGrid[fact.gridPosition.row][fact.gridPosition.col] = fact;
        initialRevealedIds.add(fact.id);
      } else {
        available.push(fact);
      }
    });

    setGrid(newGrid);
    setAvailableFacts(available);
    setRevealed({ rows: [false, false, false], cols: [false, false, false] });
    setMoves(0);
    setTime(0);
    setIsComplete(false);
    setRevealedFactIds(initialRevealedIds);
  };

  // Check if a row is complete and correct
  const checkRow = useCallback(
    (rowIdx: number, currentGrid: GridState): boolean => {
      const row = currentGrid[rowIdx];
      if (row.some((cell) => cell === null)) return false;
      const sum = row.reduce((acc, cell) => acc + (cell?.number || 0), 0);
      return sum === puzzle.targetSums.rows[rowIdx];
    },
    [puzzle.targetSums.rows]
  );

  // Check if a column is complete and correct
  const checkCol = useCallback(
    (colIdx: number, currentGrid: GridState): boolean => {
      const col = [currentGrid[0][colIdx], currentGrid[1][colIdx], currentGrid[2][colIdx]];
      if (col.some((cell) => cell === null)) return false;
      const sum = col.reduce((acc, cell) => acc + (cell?.number || 0), 0);
      return sum === puzzle.targetSums.cols[colIdx];
    },
    [puzzle.targetSums.cols]
  );

  // Update revealed state and check for win
  const updateRevealedState = useCallback(
    (currentGrid: GridState) => {
      const newRows: [boolean, boolean, boolean] = [
        checkRow(0, currentGrid),
        checkRow(1, currentGrid),
        checkRow(2, currentGrid),
      ];
      const newCols: [boolean, boolean, boolean] = [
        checkCol(0, currentGrid),
        checkCol(1, currentGrid),
        checkCol(2, currentGrid),
      ];

      setRevealed({ rows: newRows, cols: newCols });

      // Update revealed fact IDs (not numbers, to handle duplicates)
      const newRevealedFactIds = new Set<string>();
      
      // Add pre-placed fact IDs
      puzzle.facts.forEach((fact: Fact) => {
        if (fact.isPrePlaced) {
          newRevealedFactIds.add(fact.id);
        }
      });

      // Add fact IDs from revealed rows
      newRows.forEach((isRevealed, rowIdx) => {
        if (isRevealed) {
          currentGrid[rowIdx].forEach((cell) => {
            if (cell) newRevealedFactIds.add(cell.id);
          });
        }
      });

      // Add fact IDs from revealed columns
      newCols.forEach((isRevealed, colIdx) => {
        if (isRevealed) {
          [0, 1, 2].forEach((rowIdx) => {
            const cell = currentGrid[rowIdx][colIdx];
            if (cell) newRevealedFactIds.add(cell.id);
          });
        }
      });

      setRevealedFactIds(newRevealedFactIds);

      // Check for win
      if (newRows.every((r) => r) && newCols.every((c) => c)) {
        setIsComplete(true);
        onOpen();
      }
    },
    [checkRow, checkCol, puzzle.facts, onOpen]
  );

  // Check if a cell's number should be revealed
  const isCellRevealed = (row: number, col: number): boolean => {
    return revealed.rows[row] || revealed.cols[col];
  };

  // Drag handlers
  const handleDragStart = (e: React.DragEvent, fact: Fact) => {
    setDraggingFact(fact);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setDraggingFact(null);
    setDragOverCell(null);
    setSidebarDragOver(false);
  };

  const handleCellDragOver = (e: React.DragEvent, row: number, col: number) => {
    e.preventDefault();
    setDragOverCell({ row, col });
    setSidebarDragOver(false);
  };

  const handleCellDragLeave = () => {
    setDragOverCell(null);
  };

  const handleCellDrop = (e: React.DragEvent, row: number, col: number) => {
    e.preventDefault();
    if (!draggingFact) return;

    const targetCell = grid[row][col];

    // Can't drop on pre-placed facts
    if (targetCell?.isPrePlaced) {
      toast({
        title: "Cannot replace",
        description: "This fact is pre-placed and cannot be moved.",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      handleDragEnd();
      return;
    }

    // Create new grid
    const newGrid = grid.map((r: (Fact | null)[]) => [...r]);
    const newAvailable = [...availableFacts];

    // Remove dragging fact from its current location
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 3; c++) {
        if (newGrid[r][c]?.id === draggingFact.id) {
          newGrid[r][c] = null;
        }
      }
    }

    // Remove from available if it was there
    const availableIdx = newAvailable.findIndex((f) => f.id === draggingFact.id);
    if (availableIdx !== -1) {
      newAvailable.splice(availableIdx, 1);
    }

    // If target cell had a fact, move it back to available
    if (targetCell) {
      newAvailable.push(targetCell);
    }

    // Place the dragging fact in the target cell
    newGrid[row][col] = draggingFact;

    setGrid(newGrid);
    setAvailableFacts(newAvailable);
    setMoves((prev: number) => prev + 1);
    updateRevealedState(newGrid);
    handleDragEnd();
  };

  const handleSidebarDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setSidebarDragOver(true);
    setDragOverCell(null);
  };

  const handleSidebarDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggingFact || draggingFact.isPrePlaced) {
      handleDragEnd();
      return;
    }

    // Remove from grid if it was there
    const newGrid = grid.map((r: (Fact | null)[]) => [...r]);
    let wasInGrid = false;
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 3; c++) {
        if (newGrid[r][c]?.id === draggingFact.id) {
          newGrid[r][c] = null;
          wasInGrid = true;
        }
      }
    }

    if (wasInGrid) {
      const newAvailable = [...availableFacts, draggingFact];
      setGrid(newGrid);
      setAvailableFacts(newAvailable);
      setMoves((prev: number) => prev + 1);
      updateRevealedState(newGrid);
    }

    handleDragEnd();
  };

  // Calculate current sums
  const getCurrentRowSum = (rowIdx: number): number | null => {
    const row = grid[rowIdx];
    if (row.some((cell: Fact | null) => cell === null)) return null;
    return row.reduce((acc: number, cell: Fact | null) => acc + (cell?.number || 0), 0);
  };

  const getCurrentColSum = (colIdx: number): number | null => {
    const col = [grid[0][colIdx], grid[1][colIdx], grid[2][colIdx]];
    if (col.some((cell: Fact | null) => cell === null)) return null;
    return col.reduce((acc: number, cell: Fact | null) => acc + (cell?.number || 0), 0);
  };

  const handlePlayAgain = () => {
    onClose();
    initializeGame();
  };

  return (
    <>
      <Navbar />
      <Box
        minH="100vh"
        bg="gray.50"
        pt={{ base: "70px", md: "80px" }}
        pb={8}
        px={4}
      >
        <VStack spacing={4} maxW="1000px" mx="auto">
          {/* Header */}
          <VStack spacing={1}>
            <Heading size="lg" color="teal.600">
              By The Numbers
            </Heading>
            <Text fontSize="sm" color="gray.600">
              Drag facts to make rows and columns sum correctly
            </Text>
          </VStack>

          {/* Stats Bar */}
          <HStack spacing={6} justify="center" w="100%">
            <HStack>
              <Icon as={RepeatIcon} color="gray.500" />
              <Text fontWeight="bold">{moves} moves</Text>
            </HStack>
            <HStack>
              <Icon as={TimeIcon} color="gray.500" />
              <Text fontWeight="bold">{formatTime(time)}</Text>
            </HStack>
          </HStack>

          {/* Main Game Area */}
          <Box
            display="flex"
            flexDirection={{ base: "column", lg: "row" }}
            gap={6}
            w="100%"
            justifyContent="center"
            alignItems={{ base: "center", lg: "flex-start" }}
          >
            {/* Sidebar - Available Facts */}
            <Box order={{ base: 2, lg: 1 }}>
              <Sidebar
                availableFacts={availableFacts}
                draggingFact={draggingFact}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={handleSidebarDragOver}
                onDrop={handleSidebarDrop}
                isDragOver={sidebarDragOver}
              />
            </Box>

            {/* Grid Area */}
            <Box order={{ base: 1, lg: 2 }}>
              <VStack spacing={2}>
                {/* Grid with row sums */}
                {[0, 1, 2].map((rowIdx) => (
                  <HStack key={rowIdx} spacing={2}>
                    {[0, 1, 2].map((colIdx) => (
                      <React.Fragment key={`${rowIdx}-${colIdx}`}>
                        <GridCell
                          row={rowIdx}
                          col={colIdx}
                          fact={grid[rowIdx][colIdx]}
                          isPrePlaced={grid[rowIdx][colIdx]?.isPrePlaced || false}
                          isRevealed={isCellRevealed(rowIdx, colIdx)}
                          isDragOver={dragOverCell?.row === rowIdx && dragOverCell?.col === colIdx}
                          draggingFact={draggingFact}
                          onDragOver={(e) => handleCellDragOver(e, rowIdx, colIdx)}
                          onDragLeave={handleCellDragLeave}
                          onDrop={handleCellDrop}
                          onDragStart={handleDragStart}
                          onDragEnd={handleDragEnd}
                        />
                        {colIdx < 2 && (
                          <Text fontWeight="bold" color="gray.400" fontSize="xl">
                            +
                          </Text>
                        )}
                      </React.Fragment>
                    ))}
                    <Text fontWeight="bold" color="gray.400" fontSize="xl">
                      =
                    </Text>
                    <SumDisplay
                      target={puzzle.targetSums.rows[rowIdx]}
                      current={getCurrentRowSum(rowIdx)}
                      isCorrect={revealed.rows[rowIdx]}
                      direction="row"
                    />
                  </HStack>
                ))}

                {/* Column operation symbols */}
                <HStack spacing={2} justify="center" w="100%">
                  {[0, 1, 2].map((colIdx) => (
                    <React.Fragment key={`plus-${colIdx}`}>
                      <Box minW={{ base: "90px", md: "100px" }} textAlign="center">
                        <Text fontWeight="bold" color="gray.400" fontSize="xl">
                          +
                        </Text>
                      </Box>
                      {colIdx < 2 && <Box w="24px" />}
                    </React.Fragment>
                  ))}
                  <Box w="24px" />
                  <Box minW="50px" />
                </HStack>

                {/* Column sums */}
                <HStack spacing={2} justify="center" w="100%">
                  {[0, 1, 2].map((colIdx) => (
                    <React.Fragment key={`colsum-${colIdx}`}>
                      <Box minW={{ base: "90px", md: "100px" }} display="flex" justifyContent="center">
                        <SumDisplay
                          target={puzzle.targetSums.cols[colIdx]}
                          current={getCurrentColSum(colIdx)}
                          isCorrect={revealed.cols[colIdx]}
                          direction="col"
                        />
                      </Box>
                      {colIdx < 2 && <Box w="24px" />}
                    </React.Fragment>
                  ))}
                  <Box w="24px" />
                  <Box minW="50px" />
                </HStack>
              </VStack>
            </Box>

            {/* Number Bank */}
            <Box order={{ base: 3, lg: 3 }}>
              <NumberBank
                facts={puzzle.facts}
                revealedFactIds={revealedFactIds}
              />
            </Box>
          </Box>

          {/* Instructions */}
          <Box
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            p={4}
            maxW="600px"
            w="100%"
          >
            <Heading size="xs" mb={2}>
              How to Play
            </Heading>
            <VStack align="start" spacing={1} fontSize="sm" color="gray.600">
              <Text>• Drag facts from the sidebar into empty grid squares</Text>
              <Text>• Each fact has a hidden number (except pre-placed ones in teal)</Text>
              <Text>• Make each row and column sum to the target number</Text>
              <Text>• When a row/column is correct, the numbers reveal!</Text>
              <Text>• Complete all rows and columns to win</Text>
            </VStack>
          </Box>
        </VStack>

        {/* Win Modal */}
        <StatsModal
          isOpen={isOpen}
          onClose={onClose}
          moves={moves}
          time={time}
          onPlayAgain={handlePlayAgain}
        />
      </Box>
    </>
  );
};

export default ByTheNumbers;
